import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { getApiHostHeaders, requireApiBaseUrl } from '@/lib/api-url';
import { appendCompressedImage } from '@/lib/image-upload';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { PortfolioLimits, PortfolioPhoto } from '@/types/portfolio';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFetchErrorMessage } from '@/utils/api-error';

export const portfolioKeys = {
  all: ['provider-portfolio'] as const,
  photos: () => [...portfolioKeys.all, 'photos'] as const,
  limits: () => [...portfolioKeys.all, 'limits'] as const,
};

export function usePortfolioPhotos() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: portfolioKeys.photos(),
    queryFn: async () => {
      const response = await api.get<PortfolioPhoto[]>('/provider/me/portfolio');
      return response.data;
    },
    enabled: isHydrated,
  });
}

export function usePortfolioLimits() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: portfolioKeys.limits(),
    queryFn: async () => {
      const response = await api.get<PortfolioLimits>('/provider/me/portfolio/limits');
      return response.data;
    },
    enabled: isHydrated,
  });
}

export interface AddPortfolioPhotoInput {
  imageUri: string;
  /** Foto "antes" do par antes/depois (imageUri é o "depois"). */
  beforeImageUri?: string;
  caption?: string;
  serviceId?: string;
  /** Tamanho em bytes reportado pelo picker, quando disponível (pré-checagem local). */
  fileSizeBytes?: number;
}

export function useAddPortfolioPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddPortfolioPhotoInput) => {
      // Pré-checagem local de limites (o backend valida de novo)
      const limits = queryClient.getQueryData<PortfolioLimits>(portfolioKeys.limits());
      if (limits) {
        if (limits.remainingPhotos !== null && limits.remainingPhotos <= 0) {
          throw new PortfolioLimitError(
            `Você atingiu o limite de ${limits.maxPhotos} fotos do plano ${limits.planName}.`,
            'PORTFOLIO_PHOTO_LIMIT_EXCEEDED',
          );
        }
        if (input.fileSizeBytes && input.fileSizeBytes > limits.maxFileSizeBytes) {
          const maxMb = Math.floor(limits.maxFileSizeBytes / (1024 * 1024));
          throw new PortfolioLimitError(
            `A foto excede o tamanho máximo de ${maxMb}MB.`,
            'IMAGE_TOO_LARGE',
          );
        }
      }

      const formData = new FormData();
      await appendCompressedImage(formData, 'file', input.imageUri, 'photo');
      if (input.beforeImageUri) {
        await appendCompressedImage(formData, 'beforeFile', input.beforeImageUri, 'photo');
      }
      if (input.caption?.trim()) {
        formData.append('caption', input.caption.trim());
      }
      if (input.serviceId) {
        formData.append('serviceId', input.serviceId);
      }

      const API_URL = requireApiBaseUrl().replace(/\/$/, '');
      const token = useAuthStore.getState().token;

      const response = await fetch(`${API_URL}/provider/me/portfolio`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...getApiHostHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new PortfolioLimitError(
          response.status >= 500
            ? getFetchErrorMessage(response.status, errorData, 'Erro ao enviar foto.')
            : getPortfolioErrorMessage(errorData, 'Erro ao enviar foto.'),
          errorData?.code,
        );
      }

      return (await response.json()) as PortfolioPhoto;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.all });
    },
  });
}

export function useUpdatePortfolioPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      caption?: string | null;
      serviceId?: string | null;
      position?: number;
    }) => {
      const response = await api.patch<PortfolioPhoto>(`/provider/me/portfolio/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.photos() });
    },
  });
}

export function useDeletePortfolioPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/provider/me/portfolio/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.all });
    },
  });
}

export class PortfolioLimitError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'PortfolioLimitError';
    this.code = code;
  }
}

/** Extrai mensagem amigável dos erros estruturados da API de galeria. */
export function getPortfolioErrorMessage(errorData: any, fallback: string): string {
  const code = errorData?.code;
  if (code === 'PORTFOLIO_PHOTO_LIMIT_EXCEEDED') {
    const { maxPhotos, planName } = errorData;
    return maxPhotos != null && planName
      ? `Você atingiu o limite de ${maxPhotos} fotos do plano ${planName}. Faça upgrade para adicionar mais.`
      : 'Você atingiu o limite de fotos da galeria do seu plano.';
  }
  if (code === 'IMAGE_TOO_LARGE' || code === 'IMAGE_INVALID_TYPE') {
    return errorData.message;
  }
  return errorData?.message || fallback;
}
